<template>
  <div class="notification-center">
    <v-menu
      v-model="isOpen"
      :close-on-content-click="false"
      offset-x
      offset-y
      max-width="420px"
      left
    >
      <template v-slot:activator="{ on }">
        <div v-on="on" class="notification-btn-wrapper">
          <v-btn
            dark
            icon
            large
            class="notification-btn"
          >
            <v-icon>notifications</v-icon>
          </v-btn>
          <div v-if="unreadCount > 0" class="notification-badge">
            {{ unreadCount }}
          </div>
        </div>
      </template>

      <v-card class="notification-panel">
        <v-card-title class="notification-header">
          <div class="header-content">
            <h3>Notificaciones</h3>
            <div class="header-actions">
              <v-chip
                small
                text-color="white"
                :color="unreadCount > 0 ? 'error' : 'grey'"
              >
                {{ unreadCount }} nuevas
              </v-chip>
              <v-btn
                icon
                small
                @click="toggleSound"
                :color="soundEnabled ? 'white' : 'grey lighten-1'"
                class="ml-2"
              >
                <v-icon small>{{ soundEnabled ? 'volume_up' : 'volume_off' }}</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <!-- Lista de notificaciones -->
        <v-list v-if="notifications.length > 0" class="notification-list">
          <notification-item
            v-for="notification in sortedNotifications"
            :key="notification.id"
            :notification="notification"
            @mark-read="markAsRead"
            @delete="deleteNotification"
          />
        </v-list>

        <!-- Sin notificaciones -->
        <div v-else class="empty-state">
          <v-icon color="grey lighten-1" size="48">
            notifications_off
          </v-icon>
          <p class="empty-text">No tienes notificaciones</p>
        </div>

        <v-divider v-if="notifications.length > 0"></v-divider>

        <!-- Acciones -->
        <v-card-actions v-if="notifications.length > 0" class="notification-actions">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="primary"
            small
            @click="markAllAsRead"
          >
            Marcar todo como leído
          </v-btn>
          <v-btn
            text
            color="error"
            small
            @click="clearAll"
          >
            Limpiar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import NotificationItem from './NotificationItem.vue'
import notificationService from '../../services/notificationService'

export default {
  name: 'NotificationCenter',
  components: {
    NotificationItem
  },
  data() {
    return {
      isOpen: false,
      notifications: [],
      loading: false,
      wsConnected: false,
      soundEnabled: true
    }
  },
  computed: {
    unreadCount() {
      return this.notifications.filter(n => !n.isRead).length
    },
    sortedNotifications() {
      return [...this.notifications].sort((a, b) => {
        if (a.isRead === b.isRead) {
          return new Date(b.createdAt) - new Date(a.createdAt)
        }
        return a.isRead ? 1 : -1
      })
    }
  },
  methods: {
    async loadNotifications() {
      this.loading = true
      try {
        this.notifications = await notificationService.getNotifications()
        // Convertir createdAt a Date si viene como string
        this.notifications = this.notifications.map(n => ({
          ...n,
          createdAt: typeof n.createdAt === 'string' ? new Date(n.createdAt) : n.createdAt
        }))
      } catch (error) {
        console.error('Error cargando notificaciones:', error)
        this.$store.commit('snackbar_change_message', 'Error al cargar notificaciones')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      } finally {
        this.loading = false
      }
    },
    async markAsRead(notificationId) {
      try {
        const notification = this.notifications.find(n => n.id === notificationId)
        if (notification) {
          await notificationService.markAsRead(notificationId)
          notification.isRead = true
        }
      } catch (error) {
        console.error('Error marcando notificación como leída:', error)
        this.$store.commit('snackbar_change_message', 'Error al actualizar notificación')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      }
    },
    async deleteNotification(notificationId) {
      const index = this.notifications.findIndex(n => n.id === notificationId)
      if (index !== -1) {
        this.notifications.splice(index, 1)
      }
    },
    async markAllAsRead() {
      try {
        await notificationService.markAllAsRead()
        this.notifications.forEach(n => {
          n.isRead = true
        })
      } catch (error) {
        console.error('Error marcando todas como leídas:', error)
        this.$store.commit('snackbar_change_message', 'Error al marcar todas como leídas')
        this.$store.commit('snackbar_change_color', 'error')
        this.$store.commit('snackbar_activate_true')
      }
    },
    clearAll() {
      this.notifications = []
      this.isOpen = false
    },
    addNotification(notification) {
      const newNotification = {
        isRead: false,
        createdAt: new Date(),
        ...notification
      }
      this.notifications.unshift(newNotification)
    },
    handleWebSocketNotification(notification) {
      // Agregar notificación recibida en tiempo real
      this.addNotification(notification)
      // Reproducir sonido
      notificationService.playNotificationSound()
      // Mostrar toast
      this.$store.commit('snackbar_change_message', notification.title)
      this.$store.commit('snackbar_change_color', this.getColorByType(notification.type))
      this.$store.commit('snackbar_activate_true')
    },
    getColorByType(type) {
      const colorMap = {
        'success': 'success',
        'error': 'error',
        'warning': 'warning',
        'info': 'info'
      }
      return colorMap[type] || 'info'
    },
    toggleSound() {
      this.soundEnabled = !this.soundEnabled
      notificationService.setSoundEnabled(this.soundEnabled)
    }
  },
  created() {
    this.loadNotifications()
    // Conectar WebSocket para recibir notificaciones en tiempo real
    notificationService.connectWebSocket(this.handleWebSocketNotification)
  },
  beforeDestroy() {
    // Desconectar WebSocket y polling al destruir el componente
    notificationService.disconnectWebSocket()
    notificationService.stopPolling()
  }
}
</script>

<style scoped>
.notification-center {
  display: flex;
  align-items: center;
}

.notification-btn-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin: 0 8px;
}

.notification-btn {
  margin: 0 !important;
}

.notification-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #f44336;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.notification-panel {
  max-height: 600px;
  display: flex;
  flex-direction: column;
}

.notification-header {
  padding: 16px;
  background: linear-gradient(135deg, #002b48 0%, #003d5f 100%);
  color: white;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.header-content h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-list {
  flex: 1;
  overflow-y: auto;
  max-height: 450px;
  padding: 8px 0;
}

.notification-list::-webkit-scrollbar {
  width: 6px;
}

.notification-list::-webkit-scrollbar-track {
  background: #f5f5f5;
}

.notification-list::-webkit-scrollbar-thumb {
  background: #bdbdbd;
  border-radius: 3px;
}

.notification-list::-webkit-scrollbar-thumb:hover {
  background: #9e9e9e;
}

.empty-state {
  padding: 48px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-text {
  margin-top: 16px;
  color: #757575;
  font-size: 14px;
}

.notification-actions {
  padding: 8px 16px;
}
</style>
